import queryString from 'query-string';
import { WATERFALL_SLUG } from 'common/constants/allocations';
import { CAP_TABLE_SLUG, CAP_TABLE_TAB_SLUGS, CASH_FLOW_LEDGER_SLUG } from 'common/constants/cap-table';
import { ENTITY_QUERY_PARAM, VERSION_QUERY_PARAM } from 'common/constants/pageFilters';
import {
  OPM_CALCULATOR_QUERY_PARAMS,
  TAB_QUERY_PARAM,
} from 'pages/ValuationsAllocation/common/constants/valuations';
import { WATERFALL_QUERY_PARAM_CAP_TABLE_VERSION } from 'pages/Waterfall/constants';
import { removeWaterfallCalculationQueryParams } from 'pages/Waterfall/utilities/waterfallSearchParams';
import { isStringNumeric } from 'utilities/isStringNumeric';
import type { BuildCompanyNavigationUrlParams } from './companyNavigation.types';

interface IsCompanyMissingMeasurementDatesParams {
  companyMeasurementDateList?: unknown[] | null;
  hasMeasurementDateSlug: boolean;
  isCompanyContextLoading: boolean;
}

const omitQueryParams = (queryParams: Record<string, unknown>, paramsToRemove: string[]) =>
  Object.fromEntries(Object.entries(queryParams).filter(([param]) => !paramsToRemove.includes(param)));

export const isCompanyMissingMeasurementDates = ({
  companyMeasurementDateList,
  hasMeasurementDateSlug,
  isCompanyContextLoading,
}: IsCompanyMissingMeasurementDatesParams) => {
  if (isCompanyContextLoading || companyMeasurementDateList === null) {
    return false;
  }

  return Array.isArray(companyMeasurementDateList)
    ? companyMeasurementDateList.length === 0
    : !hasMeasurementDateSlug;
};

export const getCompanyNavigationQueryParams = ({
  currentTableSlug,
  queryParams,
  targetTableSlug,
}: Pick<BuildCompanyNavigationUrlParams, 'currentTableSlug' | 'queryParams' | 'targetTableSlug'>) => {
  let nextQuery = { ...queryParams };

  // The cap table and waterfall pages track the selected cap table version under their own
  // using different query params (`version` vs `cap-table-version`, respectively).
  const capTableVersionHandoff =
    currentTableSlug && CAP_TABLE_TAB_SLUGS.has(currentTableSlug) && targetTableSlug === WATERFALL_SLUG
      ? (nextQuery[VERSION_QUERY_PARAM] as string | undefined)
      : undefined;
  const waterfallCapTableVersionHandoff =
    currentTableSlug === WATERFALL_SLUG && targetTableSlug === CAP_TABLE_SLUG
      ? (nextQuery[WATERFALL_QUERY_PARAM_CAP_TABLE_VERSION] as string | undefined)
      : undefined;

  // A carried date query represents the selected measurement date; version/tab params
  // belong to the previous page state and should not leak into the next company tab.
  if (nextQuery.date) {
    nextQuery = omitQueryParams(nextQuery, [VERSION_QUERY_PARAM, TAB_QUERY_PARAM]);
  }

  // The entity filter only means something on the Cash Flow Ledger.
  if (targetTableSlug !== CASH_FLOW_LEDGER_SLUG) {
    nextQuery = omitQueryParams(nextQuery, [ENTITY_QUERY_PARAM]);
  }

  // OPM Calculator inputs only mean anything inside the Valuations tab, and they are scoped by
  // the `tab` param rather than namespaced per panel, so they must not follow the user out.
  if (currentTableSlug !== targetTableSlug) {
    nextQuery = omitQueryParams(nextQuery, [...OPM_CALCULATOR_QUERY_PARAMS]);
  }

  // Waterfall calculation params are only valid inside Waterfall. Leaving that tab
  // should keep shared page params but clear calculation-specific inputs.
  if (currentTableSlug === WATERFALL_SLUG && targetTableSlug !== WATERFALL_SLUG) {
    nextQuery = removeWaterfallCalculationQueryParams(nextQuery);
  }

  if (capTableVersionHandoff) {
    nextQuery = { ...nextQuery, [WATERFALL_QUERY_PARAM_CAP_TABLE_VERSION]: capTableVersionHandoff };
  }
  if (waterfallCapTableVersionHandoff) {
    const id = waterfallCapTableVersionHandoff.split('-').pop() ?? '';
    const versionId = isStringNumeric(id) ? id : waterfallCapTableVersionHandoff;
    nextQuery = { ...nextQuery, [VERSION_QUERY_PARAM]: versionId };
  }

  return nextQuery;
};

export const buildCompanyNavigationUrl = ({
  companySlug,
  currentTableSlug,
  firmSlug,
  getUrl,
  queryParams,
  targetTableSlug,
}: BuildCompanyNavigationUrlParams) => {
  // Company tabs keep their legacy company route until SCAL-8587 moves selection into the Prism page/tab model.
  const pathname = getUrl(firmSlug, companySlug);
  // Keep query retention centralized so sidebar, secondary nav, and future menu entries
  // do not drift on which tab-specific params are preserved.
  const nextQuery = getCompanyNavigationQueryParams({
    currentTableSlug,
    queryParams,
    targetTableSlug,
  });
  const search = queryString.stringify(nextQuery);

  return search ? `${pathname}?${search}` : pathname;
};
